import React, { useState, useEffect } from 'react';
import { X, AlertCircle } from 'lucide-react';
import { validateInputName, sanitizeInputName } from '../../utils/inputValidation';

interface InputNameDialogProps {
  isOpen: boolean;
  existingInputNames: string[];
  onConfirm: (name: string, description?: string) => void;
  onCancel: () => void;
}

export const InputNameDialog: React.FC<InputNameDialogProps> = ({
  isOpen,
  existingInputNames,
  onConfirm,
  onCancel,
}) => {
  const [rawName, setRawName] = useState('');
  const [description, setDescription] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  // Reset state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setRawName('');
      setDescription('');
      setValidationError(null);
    }
  }, [isOpen]);

  // Validate name as user types
  useEffect(() => {
    if (!rawName) {
      setValidationError(null);
      return;
    }

    const sanitized = sanitizeInputName(rawName);
    const validation = validateInputName(sanitized, existingInputNames);

    if (!validation.valid) {
      setValidationError(validation.error || 'Invalid name');
    } else {
      setValidationError(null);
    }
  }, [rawName, existingInputNames]);

  const handleConfirm = () => {
    const sanitized = sanitizeInputName(rawName);
    const validation = validateInputName(sanitized, existingInputNames);

    if (!validation.valid) {
      setValidationError(validation.error || 'Invalid name');
      return;
    }

    onConfirm(sanitized, description || undefined);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !validationError && rawName) {
      handleConfirm();
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  if (!isOpen) return null;

  const sanitizedName = sanitizeInputName(rawName);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Create Custom Input
          </h2>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Input Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Input Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={rawName}
              onChange={(e) => setRawName(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="e.g., requirements, context, data"
              autoFocus
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md 
                       bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                       focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            
            {/* Sanitized preview */}
            {rawName && sanitizedName && (
              <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Will be saved as: <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">{sanitizedName}</code>
              </div>
            )}
            
            {/* Validation error */}
            {validationError && (
              <div className="mt-1 flex items-start gap-1 text-xs text-red-600 dark:text-red-400">
                <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                <span>{validationError}</span>
              </div>
            )}
          </div>

          {/* Description (optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description (optional)
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Brief description of this input"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md 
                       bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                       focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Info */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded p-3 text-sm text-blue-900 dark:text-blue-200">
            <p className="font-medium mb-1">Rules:</p>
            <ul className="list-disc list-inside space-y-0.5 text-xs">
              <li>Lowercase only, no spaces</li>
              <li>Max 32 characters</li>
              <li>Alphanumeric and underscores only</li>
              <li>Cannot start with a number</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 
                     hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!rawName || !!validationError}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 
                     hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed 
                     rounded-md transition-colors"
          >
            Create Input
          </button>
        </div>
      </div>
    </div>
  );
};

